import {
  memo,
  useCallback,
  useMemo,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Users } from "lucide-react";
import { Bubble } from "@/components/diagrams/Bubble";
import { SmokeCanvas } from "@/components/fx/SmokeCanvas";
import { useBubbleTooltip } from "@/components/diagrams/useBubbleTooltip";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import {
  resolveHawModes,
  resolveWorkshopDiagram,
} from "@/features/workshop/resolve-workshop-diagram";
import { type HawMode, type HawModeId } from "./haw-content";
import { labelLayout } from "./haw-label-layout";
import { useWorkshopModals } from "./modals/useWorkshopModals";

/**
 * HAW (Human-Agent Ways of Working) bubble diagram. Ported from the
 * SVG block in `_legacy/wd.html` lines 712-818.
 *
 * Per-quadrant accents (matching the vanilla source):
 *   - TL  Human In-The-Loop      true-blue (#4995FF)
 *   - TR  Humans Above-The-Loop  gold      (#ffae41)
 *   - BL  Human On-The-Loop      peach     (#FFC982)
 *   - BR  Out-Of-The-Loop        melon     (#FF7E51)
 *   - C   Agentic Enterprise     sky       (#A1E6FF)
 *
 * Accents resolve via the `mode.accent` field on each `HAW_MODES`
 * entry (see `./haw-content.ts`), so they theme-swap automatically
 * through the `--accent-*` CSS variables.
 *
 * Slice 2 restyle: wrapped in the legacy `.panel` chrome (panel-header
 * with `group` icon — mapped to lucide `Users` — + title + ··· menu;
 * panel-body holds the SVG diagram; `.haw-status` footer below).
 * The bubble geometry, tooltip handlers and rings are unchanged.
 *
 * Each outer bubble connects to the centre with a dashed line tinted
 * to its quadrant accent and carries a spinning dashed ring via
 * `.bubble-dash-ring` from `src/index.css` (transform-box setup
 * already correct for SVG circles). We override the default 110s
 * spin speed with the 70s the vanilla SVG uses (see the inline
 * `--bubble-ring-speed` style on each spinning ring).
 *
 * Hover tooltips are driven by the shared `useBubbleTooltip` store
 * (the same singleton overlay every other XAES diagram uses). The
 * tooltip accent is the per-mode value, so each bubble's tooltip
 * chrome matches its quadrant.
 *
 * Click-through: the SVG body is wrapped in a `<button>` that opens
 * the HAW modal — vanilla called `openHAWModal()`
 * (`_legacy/wd.html` line 712); the React port opens the modal via
 * the shared `useWorkshopModals` store with the "haw" key. See
 * `./HawModal.tsx` for the modal itself.
 */

const SKY_RGB: [number, number, number] = [161, 230, 255];

/** Geometry constants — matched to the vanilla viewBox, then padded
 *  vertically so enlarged dashboard rings aren't clipped by the
 *  footer band (bottom bubbles sit near cy≈243 with r≈78). */
const VIEW_W = 480;
const VIEW_H = 348;
const CENTRE = { cx: 240, cy: 168 };
const OUTER_R = 78;
const OUTER_RING_R = 68;
const CENTRE_R = 88;
const CENTRE_RING_R = 76;

const TITLE_LETTER_SPACING = 0.15;
const CENTRE_FONT_SIZE = 12;
const CENTRE_LETTER_SPACING = 0.2;
const CENTRE_LINE_GAP = 16;

type BubblePos = { cx: number; cy: number };

const BUBBLE_POSITIONS: Record<Exclude<HawModeId, "centre">, BubblePos> = {
  tl: { cx: 108, cy: 90 },
  tr: { cx: 372, cy: 90 },
  bl: { cx: 108, cy: 250 },
  br: { cx: 372, cy: 250 },
};

/**
 * Override the default `.bubble-dash-ring` 110s spin with the 70s the
 * vanilla SVG used. CSS custom-property values aren't part of the
 * `CSSProperties` type, so we cast through `Record<string, string>`.
 */
const RING_SPEED_STYLE = {
  "--bubble-ring-speed": "70s",
} as unknown as CSSProperties;

type OuterBubbleProps = {
  mode: HawMode;
  pos: BubblePos;
};

function bubbleTitleLines(title: string): string[] {
  // Fit long HAW mode names inside ~r=78 rings. Prefer curated wraps
  // for the four known modes; otherwise split on the last space.
  const trimmed = title.toUpperCase();
  const curated: Record<string, string[]> = {
    "HUMAN IN-THE-LOOP": ["HUMAN", "IN-THE-LOOP"],
    "HUMANS ABOVE-THE-LOOP": ["HUMANS", "ABOVE-THE", "LOOP"],
    "HUMAN ON-THE-LOOP": ["HUMAN", "ON-THE-LOOP"],
    "OUT-OF-THE-LOOP": ["OUT-OF-THE", "LOOP"],
    "AGENTIC ENTERPRISE": ["AGENTIC", "ENTERPRISE"],
  };
  if (curated[trimmed]) return curated[trimmed];
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return [trimmed];
  return [trimmed.slice(0, lastSpace), trimmed.slice(lastSpace + 1)];
}

/** Pick a font size that keeps the longest line inside the outer ring. */
function bubbleTitleFontSize(lines: string[], radius: number): number {
  const longest = lines.reduce((n, l) => Math.max(n, l.length), 0);
  // ~0.58 × fontSize ≈ average Inter bold glyph width; leave 18%
  // radial padding so glyphs don't kiss the stroke.
  const maxWidth = radius * 2 * 0.72;
  const byWidth = maxWidth / Math.max(1, longest * 0.58);
  return Math.max(9, Math.min(12, Math.floor(byWidth * 10) / 10));
}

/**
 * Wire one HAW ring/bubble to the shared tooltip store. Mirrors the
 * onEnter / onMove / onLeave pattern from
 * `src/components/diagrams/Bubble.tsx`, but passes the richer 4-field
 * payload (badge + subtitle in addition to title + body) used by the
 * vanilla wd.html HAW tooltip.
 */
function useBubbleTipHandlers(mode: HawMode) {
  const showTip = useBubbleTooltip((s) => s.show);
  const hideTip = useBubbleTooltip((s) => s.hide);
  const hasTip = mode.title.length > 0;

  const onEnter = useCallback(
    (e: ReactMouseEvent<SVGGElement>) => {
      if (!hasTip) return;
      showTip({
        title: mode.title,
        body: mode.body,
        badge: mode.badge,
        subtitle: mode.subtitle,
        accent: mode.accent,
        x: e.clientX,
        y: e.clientY,
      });
    },
    [hasTip, showTip, mode],
  );

  const onMove = useCallback(
    (e: ReactMouseEvent<SVGGElement>) => {
      if (!hasTip) return;
      showTip({
        title: mode.title,
        body: mode.body,
        badge: mode.badge,
        subtitle: mode.subtitle,
        accent: mode.accent,
        x: e.clientX,
        y: e.clientY,
      });
    },
    [hasTip, showTip, mode],
  );

  const onLeave = useCallback(() => {
    if (!hasTip) return;
    hideTip();
  }, [hasTip, hideTip]);

  return {
    onMouseEnter: hasTip ? onEnter : undefined,
    onMouseMove: hasTip ? onMove : undefined,
    onMouseLeave: hasTip ? onLeave : undefined,
    hasTip,
  };
}

function OuterBubble({ mode, pos }: OuterBubbleProps) {
  const lines = bubbleTitleLines(mode.title);
  const fontSize = bubbleTitleFontSize(lines, OUTER_R);
  const layout = labelLayout(
    pos.cx,
    pos.cy,
    lines.length,
    fontSize,
    TITLE_LETTER_SPACING,
  );
  const handlers = useBubbleTipHandlers(mode);
  // `color-mix` lets us derive a low-alpha tint from any CSS colour
  // (including theme-aware `var(--accent-x)` references) without
  // needing a parallel `-rgb` token. Modern Chrome / Edge / Firefox
  // all support it.
  const tint = `color-mix(in srgb, ${mode.accent} 6%, transparent)`;
  // Lucide icon for this bubble (legacy Material Symbols glyph
  // equivalent). Centre mode has no icon; outer bubbles do. Rendered
  // inline inside the SVG via the `x`/`y`/`width`/`height` SVG attrs
  // — lucide-react spreads these onto the underlying `<svg>`.
  return (
    <g
      className={handlers.hasTip ? "cursor-pointer" : undefined}
      onMouseEnter={handlers.onMouseEnter}
      onMouseMove={handlers.onMouseMove}
      onMouseLeave={handlers.onMouseLeave}
    >
      <circle
        cx={pos.cx}
        cy={pos.cy}
        r={OUTER_R}
        fill={tint}
        stroke={mode.accent}
        strokeWidth={1.5}
        opacity={0.7}
      />
      <circle
        className="bubble-dash-ring"
        style={RING_SPEED_STYLE}
        cx={pos.cx}
        cy={pos.cy}
        r={OUTER_RING_R}
        fill="none"
        stroke={mode.accent}
        strokeWidth={1}
        strokeDasharray="3,4"
        opacity={0.4}
      />
      {lines.map((line, i) => (
        <text
          key={line}
          x={layout.x}
          y={layout.ys[i]}
          textAnchor="middle"
          fill={mode.accent}
          fontFamily="Inter, sans-serif"
          fontWeight={700}
          fontSize={fontSize}
          letterSpacing={TITLE_LETTER_SPACING}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

const centreLines = ["AGENTIC", "ENTERPRISE"];
const centreLayout = labelLayout(
  CENTRE.cx,
  CENTRE.cy,
  centreLines.length,
  CENTRE_FONT_SIZE,
  CENTRE_LETTER_SPACING,
  CENTRE_LINE_GAP,
);

function CentreBubble({ mode }: { mode: HawMode }) {
  const handlers = useBubbleTipHandlers(mode);
  const tint = `color-mix(in srgb, ${mode.accent} 5%, transparent)`;
  return (
    <g
      className={handlers.hasTip ? "cursor-pointer" : undefined}
      onMouseEnter={handlers.onMouseEnter}
      onMouseMove={handlers.onMouseMove}
      onMouseLeave={handlers.onMouseLeave}
    >
      <circle
        cx={CENTRE.cx}
        cy={CENTRE.cy}
        r={CENTRE_R}
        fill={tint}
        stroke={mode.accent}
        strokeWidth={1.5}
      />
      <circle
        className="bubble-dash-ring"
        style={RING_SPEED_STYLE}
        cx={CENTRE.cx}
        cy={CENTRE.cy}
        r={CENTRE_RING_R}
        fill="none"
        stroke={mode.accent}
        strokeWidth={0.8}
        strokeDasharray="2,3"
        opacity={0.5}
      />
      {centreLines.map((line, i) => (
        <text
          key={line}
          x={centreLayout.x}
          y={centreLayout.ys[i]}
          textAnchor="middle"
          fill={mode.accent}
          fontFamily="Inter, sans-serif"
          fontWeight={700}
          fontSize={CENTRE_FONT_SIZE}
          letterSpacing={CENTRE_LETTER_SPACING}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

function HawPanelImpl() {
  const override = useEffectiveWorkshopOverride("WS4");
  const modes = useMemo(() => resolveHawModes(override), [override]);
  const resolved = useMemo(
    () => resolveWorkshopDiagram("WS4", override),
    [override],
  );

  const outerModes = modes.filter(
    (m): m is HawMode & { id: Exclude<HawModeId, "centre"> } =>
      m.id !== "centre" && m.id in BUBBLE_POSITIONS,
  );
  const centreMode = modes.find((m) => m.id === "centre");

  const diagramSvg = resolved.fromOverride ? (
    <svg
      viewBox={resolved.viewBox}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      className="block h-full w-full"
      role="img"
      aria-label="Human-Agent Ways of Working diagram"
    >
      {resolved.connectors.map(([x1, y1, x2, y2], i) => (
        <line
          key={`con-${i}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="var(--accent-sky)"
          strokeOpacity={0.35}
          strokeWidth={1}
          strokeDasharray="4,3"
        />
      ))}
      {resolved.bubbles.map((config) => (
        <Bubble key={config.id} config={config} />
      ))}
    </svg>
  ) : (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      className="block h-full w-full"
      role="img"
      aria-label="Four HAW modes connected to a central Agentic Enterprise concept"
    >
      {outerModes.map((mode) => {
        const pos = BUBBLE_POSITIONS[mode.id];
        return (
          <line
            key={`con-${mode.id}`}
            x1={pos.cx}
            y1={pos.cy}
            x2={CENTRE.cx}
            y2={CENTRE.cy}
            stroke={mode.accent}
            strokeWidth={1}
            strokeDasharray="4,3"
            opacity={0.35}
          />
        );
      })}

      {outerModes.map((mode) => (
        <OuterBubble
          key={mode.id}
          mode={mode}
          pos={BUBBLE_POSITIONS[mode.id]}
        />
      ))}

      {centreMode && <CentreBubble mode={centreMode} />}
    </svg>
  );

  return (
    <div className="panel">
      <div className="panel-header">
        <div className="panel-title">
          <Users className="panel-title-icon" aria-hidden />
          Human-Agent Ways of Working
        </div>
        <div className="panel-menu" aria-hidden>···</div>
      </div>
      <div
        className="panel-body relative flex min-h-0 flex-col overflow-hidden"
        style={{ padding: "6px 8px 4px" }}
      >
        <SmokeCanvas
          className="pointer-events-none absolute inset-0 h-full w-full"
          color={SKY_RGB}
          ariaLabel="HAW panel ambient smoke field"
        />
        {/*
          Click → open HAW modal. Vanilla called `openHAWModal()` on
          this click (`_legacy/wd.html` line 712); the React port
          opens the modal via the shared `useWorkshopModals` store
          with the "haw" key. Read directly off the store via
          `getState()` — we don't subscribe here because the panel
          doesn't need to react to `active` changes.

          Sizing chain (mirrors `_legacy/xaes.css` `.haw-panel-body`
          + `.haw-diagram` + `.ae-svg`):
            panel  → panel-body (flex col, min-h-0)
                   → button     (flex-1, h-full, w-full)
                   → svg        (h-full, w-full, preserveAspectRatio
                                  fits the bubble diagram into the
                                  cell without distortion).
        */}
        <button
          type="button"
          onClick={() => useWorkshopModals.getState().open("haw")}
          className="relative block h-full w-full flex-1 cursor-pointer border-0 bg-transparent p-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-sky)] focus-visible:ring-offset-2"
          aria-label="Open Human-Agent Ways of Working details"
        >
          {diagramSvg}
        </button>
      </div>
      {/* Footer — legacy `_legacy/wd.html` line 821. */}
      <div className="haw-status">
        <span className="haw-status-dot" aria-hidden />
        Ops Model Blueprint
      </div>
    </div>
  );
}

export const HawPanel = memo(HawPanelImpl);
